import React, { useState, useRef, useEffect } from 'react';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';

interface DatePickerProps {
    value: string;
    onChange: (date: string) => void;
    placeholder?: string;
    className?: string;
    position?: 'left' | 'right';
}

export const DatePicker: React.FC<DatePickerProps> = ({
    value,
    onChange,
    placeholder = 'yyyy/mm/dd',
    className = '',
    position = 'left'
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [currentDate, setCurrentDate] = useState(() => {
        if (value) {
            const date = new Date(value);
            if (!isNaN(date.getTime())) return date;
        }
        return new Date();
    });
    const containerRef = useRef<HTMLDivElement>(null);

    // Sync currentDate when value changes externally (if valid)
    useEffect(() => {
        if (value) {
            const date = new Date(value);
            if (!isNaN(date.getTime())) {
                setCurrentDate(date);
            }
        }
    }, [value]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const daysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = (year: number, month: number) => new Date(year, month, 1).getDay();

    const handlePrevMonth = (e: React.MouseEvent) => {
        e.stopPropagation();
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    };

    const handleNextMonth = (e: React.MouseEvent) => {
        e.stopPropagation();
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    };

    const handleSelectDate = (year: number, month: number, day: number, e: React.MouseEvent) => {
        e.stopPropagation();
        const date = new Date(year, month, day);
        // Format to YYYY-MM-DD
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        onChange(`${y}-${m}-${d}`);
        setIsOpen(false);
    };

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        onChange('');
        setIsOpen(false);
    };

    const handleToday = (e: React.MouseEvent) => {
        e.stopPropagation();
        const today = new Date();
        const y = today.getFullYear();
        const m = String(today.getMonth() + 1).padStart(2, '0');
        const d = String(today.getDate()).padStart(2, '0');
        onChange(`${y}-${m}-${d}`);
        setCurrentDate(today);
        setIsOpen(false);
    };

    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const totalDays = daysInMonth(year, month);
    const startDay = firstDayOfMonth(year, month);
    const monthNames = ["一月", "二月", "三月", "四月", "五月", "六月", "七月", "八月", "九月", "十月", "十一月", "十二月"];

    const weekdays = ['日', '一', '二', '三', '四', '五', '六'];

    // Parse selected value for highlighting
    let selectedDateObj: Date | null = null;
    if (value) {
        const d = new Date(value);
        if (!isNaN(d.getTime())) selectedDateObj = d;
    }

    const today = new Date();

    return (
        <div className={`relative ${className}`} ref={containerRef}>
            <div
                className="flex items-center justify-between w-full cursor-pointer h-full group"
                onClick={() => setIsOpen(!isOpen)}
            >
                <div className="flex-1 min-w-0 pr-2 pb-0">
                    {/* The displayed text mimics the original input */}
                    <span className={`block w-full text-base font-bold truncate ${!value ? 'text-gray-400' : 'text-gray-900'}`}>
                        {value || placeholder}
                    </span>
                </div>
                <CalendarIcon className={`flex-shrink-0 w-4 h-4 transition-colors ${isOpen ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-600'}`} />
            </div>

            {isOpen && (
                <div className={`absolute top-full mt-2 bg-white rounded-2xl shadow-[0_10px_40px_-10px_rgba(0,0,0,0.1)] border border-gray-100 p-4 z-50 w-[270px] transform transition-all ${position === 'right' ? 'right-0 origin-top-right' : 'left-0 origin-top-left'}`}>
                    {/* Calendar Header */}
                    <div className="flex items-center justify-between mb-4 px-1">
                        <span className="text-[14px] font-bold text-gray-900 tracking-wide">
                            {year}年 {monthNames[month]}
                        </span>
                        <div className="flex gap-1">
                            <button
                                type="button"
                                onClick={handlePrevMonth}
                                className="p-1 hover:bg-gray-100 rounded-lg transition-colors text-gray-400 hover:text-gray-900 focus:outline-none"
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </button>
                            <button
                                type="button"
                                onClick={handleNextMonth}
                                className="p-1 hover:bg-gray-100 rounded-lg transition-colors text-gray-400 hover:text-gray-900 focus:outline-none"
                            >
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    {/* Days of week */}
                    <div className="grid grid-cols-7 gap-1 mb-2">
                        {weekdays.map(day => (
                            <div key={day} className="text-center text-xs font-bold text-gray-400 py-2">
                                {day}
                            </div>
                        ))}
                    </div>

                    {/* Day Grid */}
                    <div className="grid grid-cols-7 gap-y-1 gap-x-1">
                        {Array.from({ length: startDay }).map((_, index) => (
                            <div key={`empty-${index}`} className="w-8 h-8" />
                        ))}
                        {Array.from({ length: totalDays }).map((_, index) => {
                            const day = index + 1;
                            const isToday = today.getDate() === day && today.getMonth() === month && today.getFullYear() === year;
                            const isSelected = selectedDateObj && selectedDateObj.getDate() === day && selectedDateObj.getMonth() === month && selectedDateObj.getFullYear() === year;

                            return (
                                <button
                                    key={day}
                                    type="button"
                                    onClick={(e) => handleSelectDate(year, month, day, e)}
                                    className={`
                                        flex items-center justify-center w-8 h-8 mx-auto rounded-full text-[13px] font-semibold transition-all focus:outline-none
                                        ${isSelected
                                            ? 'bg-blue-600 text-white shadow-md shadow-blue-500/20 scale-105'
                                            : isToday
                                                ? 'bg-blue-50 text-blue-600 hover:bg-blue-100 relative after:absolute after:bottom-1 after:w-1 after:h-1 after:bg-blue-600 after:rounded-full'
                                                : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                                        }
                                    `}
                                >
                                    <span>{day}</span>
                                </button>
                            );
                        })}
                    </div>

                    {/* Footer Actions */}
                    <div className="flex items-center justify-between mt-5 pt-4 border-t border-gray-100 px-1">
                        <button
                            type="button"
                            onClick={handleClear}
                            className="text-xs font-bold text-gray-400 hover:text-gray-700 transition-colors focus:outline-none"
                        >
                            清除
                        </button>
                        <button
                            type="button"
                            onClick={handleToday}
                            className="text-xs font-bold text-blue-600 hover:text-blue-700 transition-colors focus:outline-none bg-blue-50 px-3 py-1.5 rounded-lg"
                        >
                            跳到今天
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};
